// @flow
import React, { Component, useEffect, useState, Suspense, lazy } from 'react';
import { ApolloProvider } from 'react-apollo';
import ApolloClient from 'apollo-client';
import { ApolloProvider as ApolloProviderRH } from '@apollo/react-hooks';
import { ApolloLink, split } from 'apollo-link';
import { BatchHttpLink } from 'apollo-link-batch-http';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { WebSocketLink } from 'apollo-link-ws';
import { getMainDefinition } from 'apollo-utilities';
import { Switch, Route, MemoryRouter } from 'react-router-dom';
import { init as sentryInit, setTag, withScope, captureException } from '@sentry/react';
import { v4 as uuidv4 } from 'uuid';
import Loading from './components/virtual-components/Loading';
import Theme, { tokens } from './providers/Theme';
import VirtualComponentPage from './pages/VirtualComponentPage';
import GettingStarted from './pages/GettingStarted';
import ComponentLabels from './pages/ComponentLabels';
import ProjectSettings from './pages/ProjectSettings';
import NextGenLinkComponents from './components/next-gen/LinkComponents';
import NextGenComponentVersions from './pages/NextGenComponentsVersions';
import NextGenLinkComponentVersions from './components/next-gen/LinkComponentVersions';
import OctoPageSettings from './pages/OctoPageSettings';
import './App.css';
import { JiraUsersProvider } from './contexts/JiraUsersContext';
import { withHashParams, useHashParams } from './providers/hashParamsContext';

const { REACT_APP_ENVIRONMENT_TYPE, REACT_APP_SENTRY_DSN_APP } = process.env;

const Expired = lazy(() => import('./pages/Expired'));
const Unavailable = lazy(() => import('./pages/Unavailable'));

const AuthLink = (operation, next) => {
    const context = (context) => {
        const xRequestId = context?.headers?.['x-request-id'];
        return {
            ...context,
            headers: {
                ...context.headers,
                Authorization: `JWT ${window.appToken}`,
                'x-request-id': xRequestId || uuidv4(),
                'x-is-forge-graphql': window.PlatformAdapter.isForge(),
            },
        };
    };
    operation.setContext(context);

    return next(operation);
};

const initSentry = () => {
    sentryInit({
        dsn: REACT_APP_SENTRY_DSN_APP,
        environment: REACT_APP_ENVIRONMENT_TYPE,
        beforeBreadcrumb(breadcrumb, hint) {
            if (hint?.event && (breadcrumb?.category === 'ui.click' || breadcrumb?.category === 'ui.input')) {
                const { target } = hint.event;
                if (target?.outerHTML) {
                    breadcrumb.message = target.outerHTML;
                }
            }
            return breadcrumb;
        },
        ignoreErrors: ['Non-Error promise rejection captured with value', 'Cannot redefine property: googletag'],
        denyUrls: [
            // Chrome extensions
            /chrome-extension:\/\//i,
            /extensions\//i,
            /^chrome:\/\//i,
            // Safari extensions
            /safari-extension:\/\//i,
            /safari-web-extension/i,
        ],
    });
};

const createApolloClient = () => {
    const endpoint = `https://${window.appBaseUrl ?? window.location.host}`;
    const httpLink = ApolloLink.from([AuthLink, new BatchHttpLink({ uri: `${endpoint}/api/graphql` })]);
    const wsLink = new WebSocketLink({
        uri: `${endpoint.replace('https', 'wss')}/api/subscriptions`,
        options: {
            reconnect: true,
            lazy: true,
            connectionParams: () => ({ JWT: window.appToken }),
        },
    });
    const link = split(
        // split based on operation type
        ({ query }) => {
            const { kind, operation } = getMainDefinition(query);
            return kind === 'OperationDefinition' && operation === 'subscription';
        },
        wsLink,
        httpLink,
    );
    return new ApolloClient({
        link,
        cache: new InMemoryCache().restore({}),
    });
};

const AppRoutes = () => {
    return (
        <Suspense fallback={<Loading />}>
            <Switch>
                <Route path="/app" exact component={VirtualComponentPage} />
                <Route path="/app/nextgen-components" component={VirtualComponentPage} />
                <Route path="/app/nextgen-component-versions" component={NextGenComponentVersions} />
                <Route path="/app/expired" component={Expired} />
                <Route path="/app/unavailable" component={Unavailable} />
                <Route path="/app/get-started" component={GettingStarted} />

                {/* iframe in issue view */}
                <Route path="/app/nextgen-link-components" component={NextGenLinkComponents} />
                <Route path="/app/nextgen-link-component-versions" component={NextGenLinkComponentVersions} />
                <Route path="/app/component-labels" component={ComponentLabels} />
                <Route path="/app/octo-page-settings" component={OctoPageSettings} />
                <Route path="/app/octo-project-settings" component={ProjectSettings} />
            </Switch>
        </Suspense>
    );
};

const App = () => {
    const [apolloClient, setApolloClient] = useState();
    const { getHashParam, initialized } = useHashParams();
    const [initialEntries, setInitialEntries] = useState<string[]>();

    useEffect(() => {
        setApolloClient(createApolloClient());
    }, []);

    useEffect(() => {
        if (!initialized || initialEntries) {
            return;
        }
        (async () => {
            const path = getHashParam('path');
            const currentWindowLocation = await window.PlatformAdapter.getLocationFromCurrentModuleKey();
            const location = currentWindowLocation || '/app';
            const entry = currentWindowLocation === '/app' && path?.length > 1 ? path : location;
            setInitialEntries([entry]);
        })();
    }, [getHashParam, initialEntries, initialized]);

    if (!apolloClient || !initialEntries) return null;

    return (
        <MemoryRouter initialEntries={initialEntries}>
            <Theme.Provider value={tokens}>
                <ApolloProvider client={apolloClient}>
                    <ApolloProviderRH client={apolloClient}>
                        <JiraUsersProvider>
                            <div className="App">
                                <AppRoutes />
                            </div>
                        </JiraUsersProvider>
                    </ApolloProviderRH>
                </ApolloProvider>
            </Theme.Provider>
        </MemoryRouter>
    );
};

class SentryWrapper extends Component {
    state = {
        hasError: false,
    };

    static getDerivedStateFromError() {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidMount = () => {
        if (REACT_APP_SENTRY_DSN_APP) {
            initSentry();
        }
    };

    componentDidCatch = (error, info) => {
        if (REACT_APP_SENTRY_DSN_APP) {
            withScope((scope) => {
                scope.setExtras(info);
                captureException(error);
            });
        }
    };

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillMount() {
        try {
            window.PlatformAdapter.getCurrentUrl().then((url) => {
                if (REACT_APP_SENTRY_DSN_APP) {
                    setTag('instance', new URL(url).origin);
                }
            });
        } catch (e) {
            console.error(e);
        }
    }

    render = () => {
        if (this.state.hasError) {
            // You can render any custom fallback UI
            return <div>Something went wrong. Please refresh the page.</div>;
        }
        return this.props.children;
    };
}

const AppWithSentry = (props) => (
    <SentryWrapper>
        <App {...props} />
    </SentryWrapper>
);

export default withHashParams(AppWithSentry);
